import { localizedStrings } from '@core/strings'
import React, { useMemo } from 'react'
import { useAvailableChecklistTemplates } from '@core/react-query/features/templates/checklistTemplates'
import { TextFieldProps } from '@mui/material/TextField/TextField'
import { Section } from '@common/Section'
import { Text } from '@fundamentals'
import {
  AvailableChecklistTemplatesWrapper,
  ChecklistTemplate,
} from '@core/entities/checklist/ChecklistTemplate'
import { TemplatePickerWrapper } from '@elements/Pickers/TemplatePickerWrapper'

export type ChecklistTemplatePickerProps = TextFieldProps & {
  organisationId: number
  projectId: number
  registerId?: number
  registerItemId?: number
  fromRegisterInspection?: boolean
  isUsedForInspections?: boolean
  value: ChecklistTemplate
  onChange?: (template: ChecklistTemplate) => void
  readOnly?: boolean
}

const ChecklistTemplatePicker: React.FC<ChecklistTemplatePickerProps> = ({
  organisationId,
  projectId,
  registerId: _registerId,
  registerItemId,
  fromRegisterInspection,
  isUsedForInspections,
  value,
  onChange,
  readOnly,
  ...props
}) => {
  const isRegisterContext = !!registerItemId

  const { data } = useAvailableChecklistTemplates(
    {
      projectId,
      isUsedForInspections,
      ...(isRegisterContext && {
        filterCreatable: true,
        registerItemId,
      }),
    },
    {
      enabled:
        !!projectId &&
        (!isRegisterContext || (!!registerItemId && !fromRegisterInspection)),
      onSuccess: (data) => {
        if (registerItemId && data?.templates?.length === 1 && !value) {
          onChange(data.templates[0])
        }
      },
    },
  )

  const availableTemplates = useMemo(() => {
    if (fromRegisterInspection) {
      return AvailableChecklistTemplatesWrapper.new({
        templates: [value],
        recent: [],
      })
    }
    return data
  }, [value?.id, data, fromRegisterInspection])

  if (readOnly) {
    const templateName =
      availableTemplates?.templates?.find(
        (template) => template.id === value?.id,
      )?.name ?? value?.name

    return (
      <Section
        sx={props?.sx}
        label={
          isUsedForInspections
            ? localizedStrings.inspectionTemplate
            : localizedStrings.checklistTemplate
        }
      >
        <Text>{templateName}</Text>
      </Section>
    )
  }

  // if creating a checklist from register item, and there is only one template available, disable the picker
  const isSingleTemplateResult = registerItemId && data?.templates?.length === 1

  return (
    <TemplatePickerWrapper
      label={
        isUsedForInspections
          ? localizedStrings.inspectionTemplate
          : localizedStrings.checklistTemplate
      }
      availableTemplates={availableTemplates}
      organisationId={organisationId}
      projectId={projectId}
      entityType='CHECKLIST_TEMPLATE'
      value={value}
      disabled={isSingleTemplateResult || props.disabled}
      onChange={(templateId) => {
        const template =
          availableTemplates?.templates?.find((t) => t.id === templateId) ??
          null
        onChange(template)
      }}
      {...props}
    />
  )
}

export { ChecklistTemplatePicker }
